import { usePlanInstanceSummary } from '@/hooks/usePlanInstanceSummary';
import type { PlanInstanceSummaryResponse } from '@/lib/templateApi';

// ─── Style constants (match SeasonView) ──────────────────────────────────────
const TEAL = '0, 255, 200';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';
const CARD_BG = 'rgba(12, 16, 24, 0.9)';
const BORDER = 'rgba(255, 255, 255, 0.12)';

const glassCard: React.CSSProperties = {
  background: CARD_BG,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${BORDER}`,
};

// ─── Skeleton ────────────────────────────────────────────────────────────────
function SummarySkeleton() {
  return (
    <div className="rounded-xl p-6" style={glassCard}>
      <div className="h-4 w-36 rounded bg-white/5 animate-pulse mb-5" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-16 rounded bg-white/5 animate-pulse" />
            <div className="h-6 w-24 rounded bg-white/5 animate-pulse" />
          </div>
        ))}
      </div>
      <div className="h-3 w-32 rounded bg-white/5 animate-pulse mb-3" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-12 rounded bg-white/5 animate-pulse mb-2" />
      ))}
    </div>
  );
}

// ─── Metric cell ─────────────────────────────────────────────────────────────
function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <span className="font-mono text-[9px] uppercase tracking-wider block" style={{ color: GREY_TEXT }}>
        {label}
      </span>
      <span className="font-mono text-lg font-medium block mt-0.5" style={{ color: WHITE_TEXT }}>
        {value}
      </span>
      {sub && (
        <span className="font-mono text-[10px] block mt-0.5" style={{ color: GREY_TEXT }}>
          {sub}
        </span>
      )}
    </div>
  );
}

// ─── Sport breakdown string ──────────────────────────────────────────────────
function formatSports(workoutsPerSport: Record<string, number>): string {
  return Object.entries(workoutsPerSport)
    .sort(([, a], [, b]) => b - a)
    .map(([sport, count]) => `${sport.charAt(0).toUpperCase() + sport.slice(1)} ${count}`)
    .join(' · ');
}

// ─── Main component ──────────────────────────────────────────────────────────
interface SeasonSummaryCardProps {
  instanceId: string | null;
}

export function SeasonSummaryCard({ instanceId }: SeasonSummaryCardProps) {
  const { data, isLoading, isError } = usePlanInstanceSummary(instanceId);

  if (!instanceId) return null;

  if (isLoading) return <SummarySkeleton />;

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 rounded-xl" style={glassCard}>
        <span className="font-mono text-sm" style={{ color: GREY_TEXT }}>
          Unable to load season summary
        </span>
      </div>
    );
  }

  if (!data || data.total_workouts === 0) {
    return (
      <div className="flex items-center justify-center py-12 rounded-xl" style={glassCard}>
        <span className="font-mono text-sm" style={{ color: GREY_TEXT }}>
          No workouts scheduled yet
        </span>
      </div>
    );
  }

  const summary: PlanInstanceSummaryResponse = data;

  return (
    <div className="rounded-xl p-6" style={glassCard}>
      <h3
        className="font-mono text-[10px] uppercase tracking-wider mb-5"
        style={{ color: `rgba(${TEAL}, 0.7)` }}
      >
        Season Summary
      </h3>

      {/* Metrics row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric label="Weeks" value={`${summary.total_weeks}`} />
        <Metric label="Workouts" value={`${summary.total_workouts}`} />
        <Metric label="Training Hours" value={`${summary.estimated_training_hours} hrs`} />
        <Metric label="Sports" value="" sub={formatSports(summary.workouts_per_sport)} />
      </div>

      {/* Phase Breakdown */}
      {summary.phase_breakdown.length > 0 && (
        <>
          <h4
            className="font-mono text-[9px] uppercase tracking-wider mb-3"
            style={{ color: GREY_TEXT }}
          >
            Phase Breakdown
          </h4>
          <div className="space-y-0">
            {summary.phase_breakdown.map((phase, idx) => (
              <div
                key={phase.name}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3"
                style={{
                  borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : undefined,
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs font-medium" style={{ color: WHITE_TEXT }}>
                    {phase.name}
                  </span>
                  <span className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                    Weeks {phase.start_week}–{phase.end_week}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 sm:mt-0">
                  {phase.focus && (
                    <span className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                      Focus: {phase.focus}
                    </span>
                  )}
                  <span
                    className="font-mono text-[10px] tabular-nums"
                    style={{ color: `rgba(${TEAL}, 0.7)` }}
                  >
                    {phase.workouts} workouts
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
